import { SaveOutlined } from "@ant-design/icons";
import { message, Modal, Popconfirm, Spin } from "antd";
import { orderApi } from "api/order.api";
import { AppButton, AppInput, AppSelect } from "components/antd-ui";
import { CommentThread } from "components/Comment/CommentThread";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CommentRefType } from "types/comment";
import { Order, OrderDebtStatus, OrderStatus } from "types/order";
import { can } from "utils/auth";
import { QUOTE_PATH } from "views/Opportunity/opportunity.meta";
import { OrderDebts, type DebtRow } from "./components/OrderDebts";
import { OrderDocuments, type DocumentRow } from "./components/OrderDocuments";
import { ORDER_PATH, orderStatusOptions } from "./order.meta";
import { ProductTable, type ProductRow } from "views/Opportunity/components/ProductTable";
import "./OrderForm.scss";

type OrderDetailModalProps = {
  orderId: number;
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="orderForm__field">
    <span className="orderForm__label">{label}</span>
    <div className="orderForm__control">{children}</div>
  </div>
);

const toDocumentRows = (order?: Order): DocumentRow[] =>
  (order?.orderDocuments || []).map((orderDocument) => ({
    key: `id-${orderDocument.id}`,
    id: orderDocument.id,
    label: orderDocument.label || "",
    files: orderDocument.files || [],
  }));

const toDebtRows = (order?: Order): DebtRow[] =>
  (order?.orderDebts || []).map((orderDebt) => ({
    key: `id-${orderDebt.id}`,
    id: orderDebt.id,
    content: orderDebt.content || "",
    percent: orderDebt.percent ?? null,
    amount: orderDebt.amount ?? null,
    status: orderDebt.status || OrderDebtStatus.Unpaid,
    dueDate: orderDebt.dueDate || "",
    collectedDate: orderDebt.collectedDate || "",
  }));

const toProductRows = (order?: Order): ProductRow[] =>
  (order?.orderDetails || []).map((orderDetail) => ({
    key: `id-${orderDetail.id}`,
    id: orderDetail.id,
    description: orderDetail.description || "",
    unit: orderDetail.unit || "",
    quantity: orderDetail.quantity ?? null,
    price: orderDetail.price ?? null,
  }));

export const OrderDetailModal = observer(({ orderId, open, onClose, onChanged }: OrderDetailModalProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState<Order>();
  const [status, setStatus] = useState<OrderStatus>(OrderStatus.Kickoff);
  const [contractCode, setContractCode] = useState("");
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [debts, setDebts] = useState<DebtRow[]>([]);

  const canUpdate = can(ORDER_PATH, "UPDATE");
  const canDelete = can(ORDER_PATH, "DELETE");
  const canUpdateDebt = can(ORDER_PATH, "UPDATE_DEBT");
  const readOnly = !canUpdate;
  const totalAmount = order?.detailTotal ?? order?.opportunity?.totalAmount ?? 0;
  const productRows = toProductRows(order);
  const quoteId = order?.opportunity?.id;
  const quoteCode = order?.opportunity?.code;

  const applyOrder = useCallback((next: Order) => {
    setOrder(next);
    setStatus(next.status || OrderStatus.Kickoff);
    setContractCode(next.contractCode || "");
    setDocuments(toDocumentRows(next));
    setDebts(toDebtRows(next));
  }, []);

  const clearOrder = useCallback(() => {
    setOrder(undefined);
    setContractCode("");
    setDocuments([]);
    setDebts([]);
  }, []);

  useEffect(() => {
    if (!open || !orderId) {
      clearOrder();
      return;
    }

    let ignore = false;
    const load = async () => {
      setLoading(true);
      clearOrder();
      try {
        const res = await orderApi.findOne(orderId);
        if (!ignore) applyOrder(res.data as Order);
      } catch {
        if (!ignore) clearOrder();
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    load();

    return () => {
      ignore = true;
    };
  }, [applyOrder, clearOrder, open, orderId]);

  const ignoreProductChange = useCallback(() => undefined, []);

  const handleStatusChange = useCallback((value: OrderStatus) => {
    setStatus(value);
  }, []);

  const handleContractCodeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setContractCode(event.target.value);
  }, []);

  const handleSave = useCallback(async () => {
    if (!orderId) return;
    const missingLabel = documents.some((row) => (row.files.length || row.id) && !row.label.trim());
    if (missingLabel) {
      message.warning("Nhập loại hồ sơ");
      return;
    }

    setSaving(true);
    try {
      const res = await orderApi.update(orderId, {
        order: {
          status,
          contractCode: contractCode.trim(),
          orderDocuments: documents
            .filter((row) => row.label.trim() || row.files.length)
            .map((row) => ({
              id: row.id,
              label: row.label.trim(),
              files: row.files,
            })),
          ...(canUpdateDebt
            ? {
                orderDebts: debts
                  .filter((row) => row.content.trim() || row.percent || row.amount || row.dueDate || row.collectedDate)
                  .map((row) => ({
                    id: row.id,
                    content: row.content.trim(),
                    percent: row.percent || 0,
                    amount: row.amount || 0,
                    status: row.status,
                    dueDate: row.dueDate,
                    collectedDate: row.collectedDate,
                  })),
              }
            : {}),
        },
      });
      message.success("Lưu đơn hàng thành công");
      applyOrder(res.data as Order);
      onChanged();
    } finally {
      setSaving(false);
    }
  }, [applyOrder, canUpdateDebt, contractCode, debts, documents, onChanged, orderId, status]);

  const handleDelete = useCallback(async () => {
    if (!orderId) return;
    setSaving(true);
    try {
      await orderApi.delete(orderId);
      message.success("Xóa đơn hàng thành công");
      onChanged();
      onClose();
    } finally {
      setSaving(false);
    }
  }, [onChanged, onClose, orderId]);

  return (
    <Modal
      className="orderModal"
      open={open}
      title="Đơn hàng"
      onCancel={onClose}
      width={1200}
      style={{ top: 20 }}
      destroyOnClose
      footer={
        <div className="orderForm__actions">
          <AppButton onClick={onClose}>Đóng</AppButton>
          {canDelete && !!order && (
            <Popconfirm
              cancelText="Không"
              okText="Xóa"
              onConfirm={handleDelete}
              title="Xác nhận xóa đơn hàng?"
            >
              <AppButton loading={saving} tone="danger">
                Xóa
              </AppButton>
            </Popconfirm>
          )}
          {canUpdate && !!order && (
            <AppButton tone="primary" loading={saving} onClick={handleSave} icon={<SaveOutlined />}>
              Lưu
            </AppButton>
          )}
        </div>
      }
    >
      <Spin spinning={loading}>
        <div className="orderModal__body">
        {order && (
          <div className="orderForm">
            <section className="orderForm__section">
              <h3 className="orderForm__sectionTitle">Thông tin</h3>
              <div className="orderForm__grid">
                <Field label="Trạng thái">
                  <AppSelect
                    value={status}
                    options={orderStatusOptions}
                    onChange={handleStatusChange}
                    disabled={readOnly}
                  />
                </Field>
                <Field label="Mã đơn">
                  <span className="orderForm__value">{order.code || "--"}</span>
                </Field>
                <Field label="Mã hợp đồng">
                  {readOnly ? (
                    <span className="orderForm__value">{contractCode || "--"}</span>
                  ) : (
                    <AppInput value={contractCode} onChange={handleContractCodeChange} />
                  )}
                </Field>
                <Field label="Khách hàng">
                  <span className="orderForm__value">{order.customer?.name || "--"}</span>
                </Field>
                <Field label="Báo giá">
                  {quoteId ? (
                    <Link className="orderForm__quoteLink" to={`${QUOTE_PATH}/${quoteId}`}>
                      {quoteCode || "--"}
                    </Link>
                  ) : (
                    <span className="orderForm__value">--</span>
                  )}
                </Field>
              </div>
            </section>

            <section className="orderForm__section">
              <h3 className="orderForm__sectionTitle">Hàng hóa</h3>
              {productRows.length ? (
                <ProductTable rows={productRows} readOnly onChange={ignoreProductChange} />
              ) : (
                <span>--</span>
              )}
            </section>

            <section className="orderForm__section">
              <h3 className="orderForm__sectionTitle">Hồ sơ</h3>
              <OrderDocuments rows={documents} readOnly={readOnly} onChange={setDocuments} />
            </section>

            <section className="orderForm__section">
              <h3 className="orderForm__sectionTitle">Công nợ</h3>
              <OrderDebts rows={debts} totalAmount={totalAmount} readOnly={!canUpdateDebt} onChange={setDebts} />
            </section>

            <section className="orderForm__section">
              <h3 className="orderForm__sectionTitle">Bình luận</h3>
              <CommentThread refType={CommentRefType.Order} refId={orderId} code={order.code} />
            </section>
          </div>
        )}
        </div>
      </Spin>
    </Modal>
  );
});
